function parseLocalDate(value: string | Date): Date {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number) as [number, number, number];
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

/**
 * Formatea una fecha a formato legible en español colombiano.
 * Entrada: string ISO, YYYY-MM-DD, o Date.
 * Salida: "15 feb 2026"
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Formatea fecha + hora. Salida: "15 feb 2026, 14:30"
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Fecha larga con día de la semana. Salida: "viernes 24 de julio de 2026"
 */
export function formatDateLong(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Distancia relativa a hoy. Salida: "hace 3 días", "hoy", "ayer"
 */
export function formatRelativeToNow(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return null;

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);

  if (days < 0) return null;
  if (days === 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 30) return `hace ${days} días`;

  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} ${months === 1 ? 'mes' : 'meses'}`;

  const years = Math.floor(days / 365);
  return `hace ${years} ${years === 1 ? 'año' : 'años'}`;
}

/**
 * Año de una fecha, para agrupar. Null si no es parseable.
 */
export function getYear(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

/**
 * Solo hora. Salida: "14:30:05"
 */
export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
